import { AfterViewInit, Component, forwardRef, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService }                    from '@ngx-translate/core';
import { UIRouter }                            from '@uirouter/core';
import { Subscription }                        from 'rxjs';

import { ApiService }                  from '../api/api.service';
import { EventBusService }             from '../core/services/event-bus.service';
import { CodeTypeModel }               from '../core/models/code-type';
import { LinkComponent }               from '../table/cell-components/actions/link.component';
import { Table }                       from '../table/types/table';
import { TableService }                from '../table/table.service';
import { TableQueryParameters }        from '../table/types/table-query-parameters';
import { TransactionCenterFiltersModel } from './models/transaction-center.filters';
import { TransactionCenterOrderModel }   from './models/transaction-center.order';

@Component({
  selector: 'wor-tc-orders',
  template: require('./transaction-center.orders.component.html')
})
export class TransactionCenterOrdersComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() orders: Array<TransactionCenterOrderModel>;
  @Input() statuses: Array<CodeTypeModel>;

  filters: TransactionCenterFiltersModel;
  tableSettings: Table;

  private tableCacheKey: string;
  private subscription: Subscription;

  constructor (
    @Inject(forwardRef(() => ApiService)) private apiService: ApiService,
    @Inject(forwardRef(() => EventBusService))
    private eventBusService: EventBusService,
    @Inject(forwardRef(() => TableService)) private tableService: TableService,
    private translateService: TranslateService,
    private uiRouter: UIRouter
  ) {}

  ngOnInit(): void {
    this.tableCacheKey = this.uiRouter.stateService.current.data.tableCacheKey;

    this.filters = this.tableService.setFiltersFromCache(this.tableCacheKey, {
      endDate        : '*',
      outstandingOnly: false,
      startDate      : '*',
      status         : '*',
      value          : ''
    });

    this.tableSettings = {
      broadcast: 'filterOrders',
      cache: { enabled: true, key: this.tableCacheKey },
      columns: [
        { field: 'order_number', header: { text: this.translateService.instant('PURCHASE_ORDER_SPACE.ORDER_NUMBER') } },
        { field: 'order_date', header: { text: this.translateService.instant('GENERAL_SPACE.FIELD.DATE') }, pipe: 'globalDate' },
        {
          component: {
            bindings: { inputs: { model: 'vendor_company_name' }, outputs: { onClick: event => this.viewVendor(event) } },
            enabled: true, factory: LinkComponent
          },
          field: 'vendor_company_name',
          header: { text: this.translateService.instant('VENDOR_SPACE.SUPPLIER') },
          sortable: true
        },
        { field: 'status', header: { text: this.translateService.instant('GENERAL_SPACE.FIELD.STATUS') }, pipe: 'purchaseOrderStatus' },
        {
          component: {
            bindings: { inputs: { model: 'job_card_number' }, outputs: { onClick: event => this.viewJobCard(event) } },
            enabled: true, factory: LinkComponent
          },
          field: 'job_card_number',
          header: { text: this.translateService.instant('GENERAL_SPACE.FIELD.ATTACHED_JOB_CARD') },
          sortable: true
        },
        { classes: 'text-right', field: 'total', header: { classes: 'text-right', text: this.translateService.instant('GENERAL_SPACE.FIELD.SUBTOTAL') }, pipe: 'globalCurrency' },
        { classes: 'text-right', field: 'total_including_tax', header: { classes: 'text-right', text: this.translateService.instant('GENERAL_SPACE.FIELD.TOTAL') }, pipe: 'globalCurrency' }
      ],
      empty: { text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_ORDERS_FOUND') },
      pagination: {
        enabled: true,
        pageSize: this.tableService.getCachedPaging(this.tableCacheKey).pageSize || 10,
        pageSizes: [10, 25, 50],
        queryCount: () => this.getCount()
      },
      rows: { highlight: { enabled: true } },
      sorting: this.tableService.setSortingFromCache(this.tableCacheKey, { direction: 'desc', field: 'order_date' })
    };

    this.subscription = this.eventBusService.on<string>('searchFilterChanged').subscribe(value => {
      if (!this.uiRouter.stateService.transition) {
        this.filters.value = value;
        this.onFiltersChanged();
      }
    });
  }

  ngAfterViewInit(): void {
    const interval = setInterval(() => {
      if (typeof window['filterOrders'] === 'function') {
        clearInterval(interval);
        this.onFiltersChanged();
      }
    }, 100);
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  onFiltersChanged(): void {
    if (window['filterOrders']) {
      window['filterOrders'](this.filters);
    }
  }

  get startDateDisplay(): string { return this.filters.startDate === '*' ? '' : this.filters.startDate; }
  set startDateDisplay(value: string) { this.filters.startDate = value || '*'; this.onFiltersChanged(); }

  get endDateDisplay(): string { return this.filters.endDate === '*' ? '' : this.filters.endDate; }
  set endDateDisplay(value: string) { this.filters.endDate = value || '*'; this.onFiltersChanged(); }

  getOrders(params: TableQueryParameters): Promise<Array<TransactionCenterOrderModel>> {
    return this.apiService.get('/transaction_center/purchase_orders', [
      this.filters.startDate       || '*',
      this.filters.endDate         || '*',
      this.filters.outstandingOnly || false,
      this.filters.status          || '*',
      this.filters.value           || '*',
      params.field,
      params.direction,
      params.pageSize,
      params.offset
    ]).then((orders: Array<TransactionCenterOrderModel>) => {
      this.orders = orders;
      return orders;
    });
  }

  getCount(): Promise<number> {
    return this.apiService.get('/transaction_center/purchase_orders_count', [
      this.filters.startDate       || '*',
      this.filters.endDate         || '*',
      this.filters.outstandingOnly || false,
      this.filters.status          || '*',
      this.filters.value           || '*'
    ]).then((count) => count.purchase_orders_count);
  }

  viewJobCard(order: TransactionCenterOrderModel): void {
    this.uiRouter.stateService.go('app.invoice', {
      customer_id: order.customer_id,
      id         : order.invoice_id,
      vehicle_id : order.vehicle_id
    });
  }

  viewOrder(order: TransactionCenterOrderModel): void {
    this.uiRouter.stateService.go('app.purchase-order', {
      vendor_id: order.vendor_id,
      id       : order.id
    });
  }

  viewVendor(order: TransactionCenterOrderModel): void {
    this.uiRouter.stateService.go('app.vendor', { id: order.vendor_id });
  }
}
